import { FeathersError } from '@feathersjs/errors';
import { AttendanceData } from '@tymbe/schema/attendance.interface';
import { AttendanceResolution, Roles } from '@tymbe/schema/enums';
import { useMutation, useQueryClient } from 'react-query';
import { useShift } from '../../hooks/useShift';

import ShiftUnapprovedAttendance, {
  AttendanceScope,
  FormValues,
} from './components/ShiftAttendance/ShiftUnapprovedAttendance';
import { useUser } from '../../apiClient/ApiContext';
import { ErrorAlert, SuccessAlert } from '../../components/alerts';
import Spinner from '../../components/Spinner';
import { ApplicationData, ApplicationState, ShiftData } from '../../types/TymbeApi';
import useSearchString from '../../hooks/UrlParamsHandler/useSearchParams';
import { FavouriteIcon } from '@tymbe/ui-icons';
import CompanyInfo from './components/ShiftDetail/CompanyInfo';
import ShiftTemplateBox from './components/ShiftTemplateBox';
import { useCallback, useRef } from 'react';
import ShiftResolvedAttendance from './components/ShiftAttendance/ShiftResolvedAttendance';
import { Form, FormApi } from 'informed';
import AttendanceStats from './components/ShiftAttendance/AttendanceStats';
import ConfirmAttendanceButton from './components/ConfirmAttendanceButton';
import { isDeleted, notDeleted } from './components/utils';
import apiClient from '../../apiClient';
import Protect from '../../apiClient/Protect';
import LongLoader from '../../components/LongLoader';
import Participants from './components/ShiftDetail/Participants';
import moment from 'moment/moment';
import feathersClient from '../../apiClient';
import ReactGA from 'react-ga4';

type ShiftAttendancePageProps = {
  onSuccessCallback?: () => Promise<unknown>;
};

const ShiftAttendancePage = ({ onSuccessCallback }: ShiftAttendancePageProps) => {
  const user = useUser();
  const formApiRef = useRef<FormApi<FormValues>>(undefined);
  const [searchParams] = useSearchString();
  const shiftId = searchParams.get('shiftAttendance') || undefined;
  const queryClient = useQueryClient();
  const { data: shift } = useShift(shiftId);
  const qClient = useQueryClient();

  const onSubmitSelected = () => {
    if (!formApiRef.current) return;
    const { values } = formApiRef.current.getFormState();
    const selectedItems = Object.values(values.applications).filter((item) => item.selected);

    onSubmit(selectedItems);
  };

  const queryKey: [string, string, number] = ['shift', 'application', Number(shiftId)];

  const { mutateAsync: createAttendance, isLoading } = useMutation({
    mutationKey: ['createAttendance'],
    mutationFn: async (values: Partial<AttendanceData>[]) => {
      const url = window.location.href;
      const res = await apiClient.service('attendance').create(values);
      ReactGA.event('confirmed_shift', {
        url,
        count: values.length,
      });

      return res;
    },
    onMutate: async (values) => {
      await queryClient.cancelQueries({ queryKey });

      const previousShift = queryClient.getQueryData<ShiftData>(queryKey);

      if (!previousShift) return {};

      return { previousShift };
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'Attendance cannot be longer than 16 hours!') {
        ErrorAlert('Pro potvrzení pracovní doby delší než 16 hodin kontaktujte svého koordinátora', 10);
      } else if (err instanceof FeathersError) {
        ErrorAlert(`${err.name}: ${err.message}`, 10);
      } else {
        ErrorAlert('Při vytváření docházky došlo k neznámé chybě');
        console.error(err);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKey);
      await onSuccessCallback?.();
      SuccessAlert('Docházka byla úspěšně vytvořena');
    },
  });


  const onSubmit = useCallback(async (values: AttendanceScope[]) => {
    const attendances = values?.map(
      (vals): Partial<AttendanceData> => ({
        confirmed_time: vals.resolution === AttendanceResolution.OK ? Number(vals.confirmed_time) * 60 : 0,
        resolution: vals.resolution,
        application_id: vals.id,
        confirmed_by: user.person_id,
        job_evaluation: vals.resolution === AttendanceResolution.OK ? Number(vals.rating) : null,
      })
    );

    const blocks = values?.map(value => value.block).filter(block => block !== null);

    await createAttendance(attendances);

    if (blocks.length > 0) {
      await Promise.all(
        blocks.map(block =>
          feathersClient
            .service('company-blocked-user')
            .create(block, {
              query: { $cancelApplications: true, $notify: true },
            })
        )
      );
      await qClient.invalidateQueries(queryKey);
      await qClient.invalidateQueries(['company-blocked-user']);
    }
  }, [user.person_id, createAttendance]);

  if (!shiftId) return null;
  if (!shift) return <Spinner show className="size-6 m-auto" />;

  if (user.isCompanyUser) {
    shift.manShift?.forEach((manShift) => {
      manShift.application = manShift.application?.filter((app) => app.state !== ApplicationState.CANCELED_EARLY);
      manShift.canceledApplication = manShift.canceledApplication?.filter((app) => app.state !== ApplicationState.CANCELED_EARLY);
      manShift.activeApplication = manShift.activeApplication?.state !== ApplicationState.CANCELED_EARLY ? manShift.activeApplication : undefined;
    });
    shift.application = shift.application?.filter((app) => app.state !== ApplicationState.CANCELED_EARLY);
  }

  const latestShiftApplicationsByPersonId = shift.application?.reduce((acc: ApplicationData[], currentApplication) => {
    const existingApplication = acc.find((item) => item.person_id === currentApplication.person_id);
    if (!existingApplication) {
      acc.push(currentApplication);
    } else if (moment(currentApplication.created_at).isAfter(moment(existingApplication.created_at))) {
      acc.splice(acc.indexOf(existingApplication), 1);
      acc.push(currentApplication);
    }
    return acc;
  }, []);

  const excludeParticipants = shift?.application
    ?.filter((app) => isDeleted(app) || app.state === ApplicationState.CONFIRMED)
    .map((app) => app.person_id);

  return (
    <Form
      formApiRef={formApiRef}
      className="p-4 flex flex-col gap-4 overflow-y-auto h-full md:grid grid-cols-[400px_1fr] grid-rows-[max-content] pb-20"
    >
      {/* company info card and company info breadcrumbs container */}
      <div className="row-start-1 col-start-1 col-end-2">
        {/* company info breadcrumbs */}
        <div className="flex items-center gap-2 text-caption-xl truncate h-fit mb-4">
          {shift.company?.name}
          <FavouriteIcon
            name="chevron_right"
            className="size-4 p-0.5 text-secondary-400 bg-secondary-transparent-5 rounded-sm"
          />
          <span className="truncate">{shift.branchoffice?.parent?.name}</span>
          <FavouriteIcon
            name="chevron_right"
            className="size-4 p-0.5 text-secondary-400 bg-secondary-transparent-5 rounded-sm"
          />
          <span className="truncate">{shift.branchoffice?.name}</span>
        </div>

        {/* company info card */}
        <div className="bg-secondary-50 p-6 rounded-3xl h-fit gap-6 flex flex-col">
          <ShiftTemplateBox shift={shift} />
          <CompanyInfo shift={shift} />
          <Participants
            current={Number(shift.manShift?.filter((m) => m.activeApplication).length) || 0}
            total={shift.manShift?.length || 0}
            applications={latestShiftApplicationsByPersonId}
            exclude={excludeParticipants}
            className="text-secondary-900 bg-white"
            shift={shift}
            disabled
          />
        </div>
      </div>

      <div className="col-start-2 col-end-3 row-start-1 md:flex-col flex-col gap-4">
        <ShiftUnapprovedAttendance
          applications={
            shift.application?.filter(
              (app) =>
                notDeleted(app) &&
                app.state === ApplicationState.CONFIRMED &&
                (!app.attendance || isDeleted(app.attendance))
            ) ?? []
          }
          startDate={shift.start_time}
          endDate={shift.end_time}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />

        <ShiftResolvedAttendance
          applications={shift.application?.filter((app) => app.attendance && notDeleted(app.attendance)) ?? []}
        />
        <div className="mt-auto">
          <AttendanceStats shift={shift} />
        </div>
      </div>

      <div className="text-secondary-600 flex py-3 px-4 items-center border-b justify-end bg-secondary-50 border-secondary-100 absolute bottom-0 left-0 rounded-b-2xl w-full h-16">
        <Protect
          auth={[
            Roles.SUPER_ADMIN,
            Roles.TYMBE_ADMIN,
            Roles.TYMBE_COORDINATOR,
            Roles.COMPANY,
            Roles.BRANCHOFFICE_MANAGER,
          ]}
          redirect={false}
        >
          <ConfirmAttendanceButton onSubmitSelected={onSubmitSelected} isLoading={isLoading} />
        </Protect>
      </div>

      <LongLoader isLoading={isLoading}>Docházky se potvrzují, prosím čekejte...</LongLoader>
    </Form>
  );
};

export default ShiftAttendancePage;
